import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Loader2, Inbox, PhoneCall, BellRing, AlertTriangle, FileText, ShieldCheck, DollarSign,
  UserPlus, ListChecks, ArrowRight, KeyRound, CheckCircle2, Clock, Sparkles, Copy, ExternalLink, ScanSearch,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePresentation } from "@/context/PresentationContext";
import { useUniversalIntake } from "@/context/UniversalIntakeContext";
import { fmtCurrency } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { AddLeadDialog } from "@/components/AddLeadDialog";
import { FirstLoginTour, TourReplayButton } from "@/components/FirstLoginTour";

const WORK_TILES = [
  { key: "new_accepted", icon: Sparkles, to: "/pipeline", accent: "text-[#B28A3D]" },
  { key: "awaiting_review", icon: Inbox, to: "/intake" },
  { key: "calls_due", icon: PhoneCall, to: "/telephony", accent: "text-[#315A7D]" },
  { key: "followups_due", icon: BellRing, to: "/followups" },
  { key: "no_next_action", icon: AlertTriangle, to: "/pipeline", warn: true },
  { key: "missing_info", icon: AlertTriangle, to: "/pipeline", warn: true },
  { key: "apps_incomplete", icon: FileText, to: "/quotes" },
  { key: "underwriting", icon: ShieldCheck, to: "/requote" },
  { key: "issued", icon: CheckCircle2, to: "/policies", accent: "text-emerald-600" },
  { key: "commission_exceptions", icon: DollarSign, to: "/commissions", warn: true },
];

const STATUS_PILL = {
  active: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  pending_verification: "bg-[#B28A3D]/12 text-[#7a5d27] ring-[#B28A3D]/30",
  expired: "bg-amber-50 text-amber-700 ring-amber-200",
  disabled: "bg-slate-100 text-slate-500 ring-slate-200",
};

const COPY = {
  en: {
    greet: { morning: "Good morning", afternoon: "Good afternoon", evening: "Good evening" },
    subtitle: "Your operating cockpit — work every lead from ownership through commission.",
    selfQuoteTitle: "Your branded self-quote link",
    selfQuoteHint: "Share this page with prospects — submissions arrive as self-generated leads assigned to you (DNC/duplicate checked).",
    copyLink: "Copy link", preview: "Preview", copied: "Self-quote link copied",
    universal: "Universal Intake", review: "Review Available Leads", add: "Add a Lead", today: "Open My Today Queue",
    workToday: "My Work Today", pipeline: "My Pipeline",
    tiles: {
      new_accepted: "New Accepted Leads", awaiting_review: "Waiting for Review", calls_due: "Calls Due",
      followups_due: "Follow-Ups Due", no_next_action: "No Next Action", missing_info: "Missing Information",
      apps_incomplete: "Applications Incomplete", underwriting: "In Underwriting", issued: "Issued Policies",
      commission_exceptions: "Commission Exceptions",
    },
    pipelineEmpty: "No active pipeline leads yet — accept a lead to get started.",
    toolsHub: "My Carrier & Tools Hub", openVault: "Open Vault", essential: "Essential",
    verified: "Verified", noSystems: "No systems in your hub yet.",
    vaultHint: "Secrets stay encrypted in the Vault — this hub only shows readiness, never passwords.",
    production: "My Production & Commissions", activeProduction: "Active Production", historical: "Historical",
    policies: "policies", expected: "Expected", received: "Received",
    pendingCarrier: "Pending Carrier Payment", vendorPending: "Vendor Payout Pending", awaitingPremium: "Awaiting 1st Premium",
    toolsSuffix: "Tools Hub",
    status: { pending_verification: "Pending verification", expired: "Expired", disabled: "Disabled", active: "Verified" },
    scope: { carrier: "Carrier", agency: "Agency", quote_tool: "Quote tool", crm: "CRM", lead_vendor: "Lead vendor", licensing: "Licensing" },
  },
  es: {
    greet: { morning: "Buenos días", afternoon: "Buenas tardes", evening: "Buenas noches" },
    subtitle: "Tu centro operativo — trabaja cada prospecto desde que lo recibes hasta que cobras la comisión.",
    selfQuoteTitle: "Tu enlace de cotización con tu marca",
    selfQuoteHint: "Compártelo con prospectos — las solicitudes entran como prospectos autogenerados asignados a ti, con revisión de DNC y duplicados.",
    copyLink: "Copiar enlace", preview: "Vista previa", copied: "Enlace de cotización copiado",
    universal: "Admisión Universal", review: "Revisar Prospectos Disponibles", add: "Añadir Prospecto", today: "Abrir Mi Cola de Hoy",
    workToday: "Mi Trabajo de Hoy", pipeline: "Mi Embudo",
    tiles: {
      new_accepted: "Prospectos Nuevos Aceptados", awaiting_review: "Esperando Revisión", calls_due: "Llamadas Pendientes",
      followups_due: "Seguimientos Pendientes", no_next_action: "Sin Próxima Acción", missing_info: "Falta Información",
      apps_incomplete: "Solicitudes Incompletas", underwriting: "En Suscripción", issued: "Pólizas Emitidas",
      commission_exceptions: "Excepciones de Comisión",
    },
    pipelineEmpty: "Aún no hay prospectos activos en el embudo — acepta un prospecto para comenzar.",
    toolsHub: "Mi Centro de Aseguradoras y Herramientas", openVault: "Abrir Bóveda", essential: "Esencial",
    verified: "Verificado", noSystems: "Aún no hay sistemas en tu centro.",
    vaultHint: "Los secretos permanecen cifrados en la Bóveda — aquí solo se muestra el estado, nunca las contraseñas.",
    production: "Mi Producción y Comisiones", activeProduction: "Producción Activa", historical: "Histórico",
    policies: "pólizas", expected: "Esperado", received: "Recibido",
    pendingCarrier: "Pago de Aseguradora Pendiente", vendorPending: "Pago a Proveedor Pendiente", awaitingPremium: "Esperando 1ra Prima",
    toolsSuffix: "Centro de Herramientas",
    status: { pending_verification: "Verificación pendiente", expired: "Vencido", disabled: "Desactivado", active: "Verificado" },
    scope: { carrier: "Aseguradora", agency: "Agencia", quote_tool: "Herramienta de cotización", crm: "CRM", lead_vendor: "Proveedor de prospectos", licensing: "Licencias" },
  },
};

const STAGE_ES = {
  "New Accepted Lead": "Prospecto Nuevo Aceptado", "New Lead": "Prospecto Nuevo", Contacted: "Contactado",
  "Fact Find Complete": "Análisis Completo", "Ready to Quote": "Listo para Cotizar", Quoted: "Cotizado",
  "Application Started": "Solicitud Iniciada", "Application Submitted": "Solicitud Enviada",
  "Pending Underwriting": "Suscripción Pendiente", "Approved as Quoted": "Aprobado como Cotizado",
  "Approved - Higher Premium": "Aprobado - Prima Mayor", "Approved - Lower Face Amount": "Aprobado - Beneficio Menor",
  "Declined - Requote Needed": "Declinado - Requiere Recotización", "Issued / Active": "Emitida / Activa",
  "Follow-Up Due": "Seguimiento Pendiente", "Client Declined": "Cliente Rechazó", "Closed Lost": "Cerrado / Perdido",
};

export default function AgentCommandCenter() {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const { isDemo } = usePresentation();
  const { openIntake } = useUniversalIntake();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const lang = i18n.language?.startsWith("es") ? "es" : "en";
  const c = COPY[lang];

  const load = useCallback(() => {
    setData(null);
    api.get(`/command-center/agent?mode=${isDemo ? "demo" : "live"}`)
      .then(({ data }) => setData(data)).catch(() => setData({ error: true }));
  }, [isDemo]);
  useEffect(load, [load]);

  if (!data) return <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  const hour = new Date().getHours();
  const greet = hour < 12 ? c.greet.morning : hour < 18 ? c.greet.afternoon : c.greet.evening;
  const w = data.work_today || {};
  const p = data.production || {};
  const stageLabel = (stage) => lang === "es" ? (STAGE_ES[stage] || stage) : stage;
  const scopeLabel = (scope) => c.scope[scope] || String(scope || "").replace("_", " ");
  const statusLabel = (status) => c.status[status] || String(status || "").replace("_", " ");

  return (
    <div className="space-y-6">
      <FirstLoginTour storageKey="fo_tour_agent" />

      <div className="flex flex-wrap items-start justify-between gap-3" data-testid="agent-command-center-header">
        <div>
          {data.agency_name && data.agency_name !== "Fortunate Operations" && (
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#B28A3D]" data-testid="agency-identity">{data.agency_name}</div>
          )}
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">{greet}, {(data.agent_name || user.name).split(" ")[0]}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{c.subtitle}</p>
        </div>
        <TourReplayButton storageKey="fo_tour_agent" />
      </div>

      {data.agency_slug && !isDemo && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-[#B28A3D]/30 bg-[#B28A3D]/5 p-4" data-testid="self-quote-card">
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[#7a5d27]">{c.selfQuoteTitle}</div>
            <div className="font-mono text-sm truncate">{`${window.location.origin}/quote/${data.agency_slug}`}</div>
            <div className="text-[11px] text-muted-foreground">{c.selfQuoteHint}</div>
          </div>
          <Button size="sm" variant="outline" data-testid="copy-quote-link"
            onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/quote/${data.agency_slug}`); toast.success(c.copied); }}>
            <Copy className="mr-1.5 h-4 w-4" /> {c.copyLink}
          </Button>
          <Button size="sm" data-testid="open-quote-link" className="bg-[#17324D] hover:bg-[#22405f]"
            onClick={() => window.open(`/quote/${data.agency_slug}`, "_blank", "noopener")}>
            <ExternalLink className="mr-1.5 h-4 w-4" /> {c.preview}
          </Button>
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="start-working">
        <Button onClick={() => openIntake()} data-testid="cta-universal-intake" className="bg-[#B28A3D] text-[#17324D] hover:bg-[#c99b45]"><ScanSearch className="mr-1.5 h-4 w-4" /> {c.universal}</Button>
        <Button onClick={() => navigate("/intake")} data-testid="cta-review-leads" className="bg-[#17324D] hover:bg-[#22405f]"><ListChecks className="mr-1.5 h-4 w-4" /> {c.review}</Button>
        <Button variant="outline" onClick={() => setAddOpen(true)} data-testid="cta-add-lead"><UserPlus className="mr-1.5 h-4 w-4" /> {c.add}</Button>
        <Button variant="outline" onClick={() => navigate("/followups")} data-testid="cta-today-queue"><Clock className="mr-1.5 h-4 w-4" /> {c.today}</Button>
      </div>

      <section>
        <div className="overline mb-3">{c.workToday}</div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="work-today-grid">
          {WORK_TILES.map((tile) => {
            const val = w[tile.key] ?? 0;
            const Icon = tile.icon;
            return (
              <button key={tile.key} onClick={() => navigate(tile.to)} data-testid={`tile-${tile.key}`}
                className={`group rounded-xl border bg-card p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-md ${tile.warn && val > 0 ? "border-[#B28A3D]/40" : "border-border"}`}>
                <div className="flex items-center justify-between">
                  <Icon className={`h-4 w-4 ${tile.accent || "text-muted-foreground"}`} />
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/0 group-hover:text-muted-foreground transition-colors" />
                </div>
                <div className={`mt-2 font-heading text-2xl font-bold ${tile.warn && val > 0 ? "text-[#7a5d27]" : ""}`}>{val}</div>
                <div className="text-[11px] text-muted-foreground leading-tight">{c.tiles[tile.key]}</div>
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <div className="overline mb-3">{c.pipeline}</div>
        <div className="flex gap-2 overflow-x-auto pb-2 kanban-scroll" data-testid="pipeline-flow">
          {(data.pipeline || []).map((s, i) => (
            <button key={s.stage} onClick={() => navigate("/pipeline")} data-testid={`stage-${i}`}
              className={`shrink-0 rounded-lg border px-3 py-2.5 text-left transition-colors hover:border-[#315A7D] ${s.urgent && s.count > 0 ? "border-[#B28A3D]/50 bg-[#B28A3D]/5" : "border-border bg-card"}`}>
              <div className="flex items-center gap-1.5">
                <span className="font-heading text-lg font-bold">{s.count}</span>
                {s.urgent && s.count > 0 && <span className="h-1.5 w-1.5 rounded-full bg-[#B28A3D] animate-pulse" />}
              </div>
              <div className="text-[11px] text-muted-foreground whitespace-nowrap max-w-[150px] truncate">{stageLabel(s.stage)}</div>
            </button>
          ))}
          {(data.pipeline || []).length === 0 && <p className="text-sm text-muted-foreground py-4">{c.pipelineEmpty}</p>}
        </div>
      </section>

      <div className="grid gap-5 lg:grid-cols-2">
        <section className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="overline flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5" /> {data.agency_name && data.agency_name !== "Fortunate Operations" ? `${data.agency_name} — ${c.toolsSuffix}` : c.toolsHub}</div>
            <button onClick={() => navigate("/vault")} className="text-xs text-[#315A7D] hover:underline" data-testid="open-vault">{c.openVault}</button>
          </div>
          <div className="space-y-2" data-testid="tools-hub">
            {(data.tools_hub || []).map((tool, i) => (
              <div key={i} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{tool.label}</div>
                  <div className="text-[11px] text-muted-foreground">{scopeLabel(tool.scope_type)}</div>
                </div>
                {tool.launch_essential && <span className="text-[10px] font-semibold text-[#7a5d27]">★ {c.essential}</span>}
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${STATUS_PILL[tool.status] || STATUS_PILL.disabled}`}>
                  {statusLabel(tool.status)}
                </span>
              </div>
            ))}
            {(data.tools_hub || []).length === 0 && <p className="text-sm text-muted-foreground py-3">{c.noSystems}</p>}
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground">{c.vaultHint}</p>
        </section>

        <section className="rounded-xl border border-border bg-card p-5" data-testid="production-panel">
          <div className="overline mb-3 flex items-center gap-1.5"><DollarSign className="h-3.5 w-3.5" /> {c.production}</div>
          <div className="grid grid-cols-2 gap-3">
            <ProdCard title={c.activeProduction} policies={p.active?.policies} expected={p.active?.expected} received={p.active?.received} accent copy={c} />
            <ProdCard title={c.historical} policies={p.historical?.policies} expected={p.historical?.expected} received={p.historical?.received} copy={c} />
          </div>
          <div className="mt-3 grid grid-cols-3 gap-3">
            <MiniStat label={c.pendingCarrier} value={fmtCurrency(p.pending_carrier_payment)} />
            <MiniStat label={c.vendorPending} value={fmtCurrency(p.vendor_payout_pending)} />
            <MiniStat label={c.awaitingPremium} value={p.issued_awaiting_first_premium ?? 0} />
          </div>
        </section>
      </div>

      {addOpen && <AddLeadDialog onClose={() => setAddOpen(false)} onSaved={load} />}
    </div>
  );
}

function ProdCard({ title, policies, expected, received, accent, copy }) {
  return (
    <div className={`rounded-lg border p-3 ${accent ? "border-[#315A7D]/30 bg-[#315A7D]/5" : "border-border"}`}>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{title}</div>
      <div className="font-heading text-2xl font-bold mt-0.5">{policies ?? 0}<span className="text-xs font-normal text-muted-foreground"> {copy.policies}</span></div>
      <div className="mt-1.5 text-[12px] space-y-0.5">
        <div className="flex justify-between"><span className="text-muted-foreground">{copy.expected}</span><span className="font-medium">{fmtCurrency(expected)}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">{copy.received}</span><span className="font-medium text-emerald-600">{fmtCurrency(received)}</span></div>
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground leading-tight">{label}</div>
      <div className="font-heading text-lg font-bold mt-1">{value}</div>
    </div>
  );
}
